import React from 'react';
import { Divider, MenuItem, TextField, Typography } from '@material-ui/core';
import { ReactComponent as SeekVolunteerIcon } from '../../../../../../../../assets/apps/seek_volunteer_icon.svg';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useStyles } from '../styles';
import { SEEK_REGION_LABELS, SeekRegion } from './SeekOpportunityFields';

const SEEK_VOLUNTEER_CODE_VERIFIER_KEY = 'seek_volunteer_code_verifier';
const SEEK_VOLUNTEER_REGION_KEY = 'seek_volunteer_region';

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(hash)));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

interface Props {
  fixedRegion?: SeekRegion;
}

export function SeekVolunteerAuthorizationRequest({ fixedRegion }: Props) {
  const [seekUrl, setSeekUrl] = React.useState<string | undefined>();
  const [region, setRegion] = React.useState<SeekRegion>(fixedRegion ?? 'au');
  const [isLoadingAuth, setIsLoadingAuth] = React.useState(false);
  const campfireFetch = useCampfireFetch<{ url: string }>({ defer: true });
  const apiUrl = useApiUrl();
  const classes = useStyles();

  const initiateAuth = async (selectedRegion: SeekRegion) => {
    setIsLoadingAuth(true);
    setSeekUrl(undefined);

    try {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      sessionStorage.setItem(SEEK_VOLUNTEER_CODE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(SEEK_VOLUNTEER_REGION_KEY, selectedRegion);

      const response = await campfireFetch.run({
        url: `${apiUrl}/wm/auth/uri?app=seek_volunteer&codeChallenge=${encodeURIComponent(
          codeChallenge
        )}&region=${selectedRegion}`,
        method: 'get',
      });

      setSeekUrl(response.data.data.url);
    } finally {
      setIsLoadingAuth(false);
    }
  };

  React.useEffect(() => {
    initiateAuth(region);
  }, []);

  const handleRegionChange = (newRegion: SeekRegion) => {
    setRegion(newRegion);
    initiateAuth(newRegion);
  };

  return (
    <div>
      {!fixedRegion && (
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Typography style={{ fontSize: '14px', fontWeight: 500 }}>Region:</Typography>
          <TextField
            select
            variant='outlined'
            size='small'
            value={region}
            onChange={(e) => handleRegionChange(e.target.value as SeekRegion)}
            disabled={isLoadingAuth}
            style={{ width: 180 }}
          >
            {(Object.keys(SEEK_REGION_LABELS) as SeekRegion[]).map((r) => (
              <MenuItem key={r} value={r}>
                {SEEK_REGION_LABELS[r]}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}
      {seekUrl && (
        <a className={classes.button} href={seekUrl} style={{ padding: '7px 12px' }}>
          <SeekVolunteerIcon width={20} height={20} style={{ color: '#fff' }} />
          <Divider orientation='vertical' style={{ height: '1.5rem', marginLeft: '4px', backgroundColor: '#fff' }} />
          <Typography
            style={{
              marginLeft: '4px',
              fontWeight: 600,
              fontSize: '13px',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            Login with SEEK Volunteer
          </Typography>
        </a>
      )}
    </div>
  );
}
